import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, update, push, remove, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { Package, CheckCircle, Plus, ShieldCheck, Trash2, Pencil, Check, X, PauseCircle, PlayCircle, ArrowLeft, ArrowUp } from "lucide-react";

export default function MyListings() {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  const [autoVerifiedMsg, setAutoVerifiedMsg] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [newPriceVal, setNewPriceVal] = useState("");
  const [oldListingsBanner, setOldListingsBanner] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Real-time listener — auto-updates when listings change in Firebase
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onValue(ref(db, "listings"), (snap) => {
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        const mine = all
          .filter(l => l.sellerId === currentUser.uid)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setListings(mine);
        setOldListingsBanner(mine.filter(l =>
          l.status === "active" && l.createdAt &&
          new Date(l.createdAt).getTime() < Date.now() - 30 * 24 * 60 * 60 * 1000
        ));
      } else {
        setListings([]);
      }
      setLoading(false);
    });
    return () => unsub();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time side-effects on mount: nudges, expiry notifications, analytics
  useEffect(() => {
    if (!currentUser) return;
    async function runSideEffects() {
      try {
        const snap = await get(ref(db, "listings"));
        if (!snap.exists()) return;
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        const mine = all.filter(l => l.sellerId === currentUser.uid);

        const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const oldActive = mine.filter(l =>
          l.status === "active" && l.createdAt && new Date(l.createdAt).getTime() < thirtyDaysAgo
        );
        for (const l of oldActive) {
          if (!l.lastExpiryReminderAt || l.lastExpiryReminderAt < sevenDaysAgo) {
            await push(ref(db, `notifications/${currentUser.uid}`), {
              type: "expiry_reminder",
              message: `Your listing "${l.title}" is 30+ days old — still available? Consider updating or removing it.`,
              listingId: l.id, createdAt: Date.now(), read: false,
            });
            await update(ref(db, `listings/${l.id}`), { lastExpiryReminderAt: Date.now() });
          }
        }

        // Smart seller nudge: active listing with 0 saves after 7 days
        try {
          const savedSnap = await get(ref(db, "saved"));
          const savedData = savedSnap.val() || {};
          for (const l of mine) {
            if (l.status === "active" && l.createdAt && new Date(l.createdAt).getTime() < sevenDaysAgo && !l.lastNudgeAt) {
              const saves = Object.values(savedData).filter(u => u && u[l.id]).length;
              if (saves === 0) {
                await push(ref(db, `notifications/${currentUser.uid}`), {
                  type: "seller_nudge",
                  message: `💡 Your listing "${l.title}" hasn't been saved by anyone yet. Try lowering the price or updating the photos!`,
                  listingId: l.id, createdAt: Date.now(), read: false,
                });
                await update(ref(db, `listings/${l.id}`), { lastNudgeAt: Date.now() });
              }
            }
          }
        } catch (e) { /* silent */ }

        // Auto-expire listings older than 60 days
        const sixtyDaysAgo = Date.now() - 60 * 24 * 60 * 60 * 1000;
        for (const l of mine) {
          if (l.status === "active" && l.createdAt && new Date(l.createdAt).getTime() < sixtyDaysAgo) {
            await update(ref(db, `listings/${l.id}`), { status: "expired" });
            await push(ref(db, `notifications/${currentUser.uid}`), {
              type: "expiry_reminder",
              message: `Your listing "${l.title}" has expired after 60 days. Relist it if still available!`,
              listingId: l.id, createdAt: Date.now(), read: false,
            });
          }
        }

        // Fetch analytics: saves and offers per listing
        try {
          const [savedSnap2, offersSnap] = await Promise.all([
            get(ref(db, "saved")),
            get(ref(db, "offers")),
          ]);
          const analyticsMap = {};
          const savedData2 = savedSnap2.val() || {};
          const offersData = offersSnap.val() || {};
          mine.forEach(l => {
            const saves = Object.values(savedData2).filter(u => u && u[l.id]).length;
            const offerCount = offersData[l.id] ? Object.keys(offersData[l.id]).length : 0;
            analyticsMap[l.id] = { views: l.views || 0, saves, offers: offerCount };
          });
          setAnalytics(analyticsMap);
        } catch (e) { /* silent */ }
      } catch (e) { /* silent */ }
    }
    runSideEffects();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  async function deleteListing(id) {
    if (!window.confirm("Delete this listing permanently? This cannot be undone.")) return;
    try {
      await remove(ref(db, `listings/${id}`));
      setListings(prev => prev.filter(l => l.id !== id));
    } catch (e) {  }
  }

  async function markSold(id) {
    try {
      const listing = listings.find(l => l.id === id);
      await update(ref(db, `listings/${id}`), { status: "sold" });
      const updated = listings.map(l => l.id === id ? { ...l, status: "sold" } : l);
      setListings(updated);

      // Notify buyer to rate the seller
      if (listing?.reservedBy) {
        await push(ref(db, `notifications/${listing.reservedBy}`), {
          type: "rate_seller",
          message: `You bought "${listing.title}" — please rate the seller!`,
          listingId: id,
          sellerId: currentUser.uid,
          createdAt: Date.now(),
          read: false,
        });
      }

      // Auto-verify check: 15+ sold items + rating >= 4.0
      if (!userProfile?.verified) {
        const soldCount = updated.filter(l => l.status === "sold").length;
        const rating = userProfile?.rating || 0;
        const totalRatings = userProfile?.totalRatings || 0;
        if (soldCount >= 15 && totalRatings >= 5 && rating >= 4.0) {
          await updateUserProfile(currentUser.uid, { verified: true, idVerificationStatus: "approved", autoVerified: true });
          setAutoVerifiedMsg(true);
          setTimeout(() => setAutoVerifiedMsg(false), 5000);
        }
      }
    } catch (e) {  }
  }

  async function pauseListing(id) {
    await update(ref(db, `listings/${id}`), { status: "paused" });
    setListings(prev => prev.map(l => l.id === id ? { ...l, status: "paused" } : l));
  }

  async function resumeListing(id) {
    await update(ref(db, `listings/${id}`), { status: "active" });
    setListings(prev => prev.map(l => l.id === id ? { ...l, status: "active" } : l));
  }

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function bulkDelete() {
    if (!selectedIds.length || !window.confirm(`Delete ${selectedIds.length} listings? This cannot be undone.`)) return;
    setBulkLoading(true);
    for (const id of selectedIds) {
      await remove(ref(db, `listings/${id}`));
    }
    setListings(prev => prev.filter(l => !selectedIds.includes(l.id)));
    setSelectedIds([]);
    setSelectMode(false);
    setBulkLoading(false);
  }

  async function bulkMarkSold() {
    if (!selectedIds.length) return;
    setBulkLoading(true);
    for (const id of selectedIds) {
      await update(ref(db, `listings/${id}`), { status: "sold" });
    }
    setListings(prev => prev.map(l => selectedIds.includes(l.id) ? { ...l, status: "sold" } : l));
    setSelectedIds([]);
    setSelectMode(false);
    setBulkLoading(false);
  }

  async function relistAllExpired() {
    const expired = listings.filter(l => l.status === "expired");
    if (!expired.length || !window.confirm(`Relist all ${expired.length} expired listings? They'll show as active again.`)) return;
    const now = new Date().toISOString();
    await Promise.all(expired.map(l => update(ref(db, `listings/${l.id}`), { status: "active", lastExpiryReminderAt: null, createdAt: now })));
    setListings(prev => prev.map(l => l.status === "expired" ? { ...l, status: "active", createdAt: now } : l));
  }

  async function bumpToTop(id) {
    const now = new Date().toISOString();
    await update(ref(db, `listings/${id}`), { createdAt: now, bumpedAt: now });
    setListings(prev => prev.map(l => l.id === id ? { ...l, createdAt: now, bumpedAt: now } : l).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
  }

  async function updatePrice(listing) {
    const newPrice = Number(newPriceVal);
    if (isNaN(newPrice) || newPrice < 0) return;
    const oldPrice = listing.price || 0;
    await update(ref(db, `listings/${listing.id}`), { price: newPrice });
    setListings(prev => prev.map(l => l.id === listing.id ? { ...l, price: newPrice } : l));
    setEditingPriceId(null);
    setNewPriceVal("");
    // If price dropped, notify users who set alerts
    if (newPrice < oldPrice && newPrice > 0) {
      try {
        const alertsSnap = await get(ref(db, `priceAlerts/${listing.id}`));
        if (alertsSnap.exists()) {
          const alertUsers = Object.keys(alertsSnap.val()).filter(uid => uid !== currentUser.uid);
          for (const uid of alertUsers) {
            await push(ref(db, `notifications/${uid}`), {
              type: "price_drop",
              message: `Price dropped on "${listing.title}": ₹${oldPrice} → ₹${newPrice}`,
              listingId: listing.id,
              createdAt: Date.now(),
              read: false,
            });
          }
        }
      } catch (e) { /* silent */ }
    }
  }

  const filtered = listings.filter(l => l.status === activeTab);
  const tabCounts = {
    active: listings.filter(l => l.status === "active").length,
    paused: listings.filter(l => l.status === "paused").length,
    pending_purchase: listings.filter(l => l.status === "pending_purchase").length,
    sold: listings.filter(l => l.status === "sold").length,
    expired: listings.filter(l => l.status === "expired").length,
  };

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-white px-4 py-4 border-b sticky top-14 z-10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <h2 className="text-lg font-bold text-gray-800">My Listings</h2>
        </div>
        {filtered.length > 0 && (
          <button
            onClick={() => { setSelectMode(!selectMode); setSelectedIds([]); }}
            className={`text-xs font-semibold px-3 py-1.5 rounded-xl ${selectMode ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"}`}
          >
            {selectMode ? "Cancel" : "Select"}
          </button>
        )}
      </div>

      {/* Old listings banner */}
      {oldListingsBanner.length > 0 && (
        <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
          <span className="text-xl flex-shrink-0">⏰</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">Old listing{oldListingsBanner.length > 1 ? "s" : ""} still active</p>
            <p className="text-xs text-amber-700 mt-0.5">{oldListingsBanner.length} listing{oldListingsBanner.length > 1 ? "s are" : " is"} 30+ days old. Still available? Consider updating the price or marking as sold.</p>
          </div>
        </div>
      )}

      {/* Auto-verified celebration */}
      {autoVerifiedMsg && (
        <div className="mx-4 mt-3 bg-green-500 text-white rounded-xl p-4 flex items-center gap-3 animate-pulse">
          <ShieldCheck size={28} />
          <div>
            <p className="font-bold">Congratulations! You're now Verified!</p>
            <p className="text-xs text-green-100">You completed 15 sales with great reviews.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 pt-3 mb-3">
        <div className="bg-gray-100 rounded-xl p-1 flex gap-1 flex-wrap">
          <button onClick={() => setActiveTab("active")}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${activeTab === "active" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
            Active ({tabCounts.active})
          </button>
          <button onClick={() => setActiveTab("paused")}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${activeTab === "paused" ? "bg-white text-gray-600 shadow-sm" : "text-gray-500"}`}>
            Paused ({tabCounts.paused})
          </button>
          <button onClick={() => setActiveTab("pending_purchase")}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${activeTab === "pending_purchase" ? "bg-white text-amber-700 shadow-sm" : "text-gray-500"}`}>
            Pending ({tabCounts.pending_purchase})
          </button>
          <button onClick={() => setActiveTab("sold")}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${activeTab === "sold" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
            Sold ({tabCounts.sold})
          </button>
          <button onClick={() => setActiveTab("expired")}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition ${activeTab === "expired" ? "bg-white text-red-600 shadow-sm" : "text-gray-500"}`}>
            Expired ({tabCounts.expired})
          </button>
        </div>
      </div>

      <div className="px-4 space-y-3">
        {/* Bulk action bar */}
        {selectMode && selectedIds.length > 0 && (
          <div className="bg-blue-600 rounded-xl p-3 flex items-center justify-between">
            <p className="text-white text-sm font-semibold">{selectedIds.length} selected</p>
            <div className="flex items-center gap-2">
              <button onClick={bulkMarkSold} disabled={bulkLoading}
                className="bg-white text-blue-600 text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-50">
                ✅ Mark Sold
              </button>
              <button onClick={bulkDelete} disabled={bulkLoading}
                className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg disabled:opacity-50">
                🗑️ Delete
              </button>
            </div>
          </div>
        )}

        {/* Bulk relist for expired */}
        {activeTab === "expired" && filtered.length > 0 && (
          <button
            onClick={relistAllExpired}
            className="w-full bg-blue-50 border border-blue-200 text-blue-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-100 transition flex items-center justify-center gap-2"
          >
            ▶️ Relist All Expired ({filtered.length})
          </button>
        )}

        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Package size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No {activeTab} listings</p>
            {activeTab === "active" && (
              <Link to="/sell" className="mt-4 inline-block bg-blue-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
                Post a Listing
              </Link>
            )}
          </div>
        ) : filtered.map(l => (
          <div key={l.id} className={`bg-white rounded-xl shadow-sm overflow-hidden flex border transition ${selectMode && selectedIds.includes(l.id) ? "border-blue-400 bg-blue-50" : "border-gray-100"}`}
            onClick={selectMode ? () => toggleSelect(l.id) : undefined}
          >
            {selectMode && (
              <div className="flex items-center justify-center px-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedIds.includes(l.id) ? "bg-blue-600 border-blue-600" : "border-gray-300"}`}>
                  {selectedIds.includes(l.id) && <Check size={12} className="text-white" />}
                </div>
              </div>
            )}
            <div className="w-20 h-20 bg-gray-100 flex-shrink-0 overflow-hidden">
              {l.imageUrl
                ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center"><Package size={24} className="text-gray-300" /></div>}
            </div>
            <div className="p-3 flex-1 flex flex-col justify-between min-w-0">
              <div>
                <p className="font-semibold text-gray-800 text-sm line-clamp-1">{l.title}</p>
                {editingPriceId === l.id ? (
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-gray-500 text-xs">₹</span>
                    <input
                      type="number"
                      value={newPriceVal}
                      onChange={e => setNewPriceVal(e.target.value)}
                      className="w-20 border border-blue-300 rounded-lg px-2 py-0.5 text-xs focus:ring-1 focus:ring-blue-400"
                      autoFocus
                    />
                    <button onClick={() => updatePrice(l)} className="p-0.5 text-green-600"><Check size={13} /></button>
                    <button onClick={() => setEditingPriceId(null)} className="p-0.5 text-red-400"><X size={13} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <p className="text-blue-600 font-bold text-sm">{l.price === 0 ? "Negotiable" : `₹${l.price}`}</p>
                    {l.status === "active" && (
                      <button onClick={() => { setEditingPriceId(l.id); setNewPriceVal(l.price || ""); }} className="p-0.5 text-gray-400 hover:text-blue-500">
                        <Pencil size={11} />
                      </button>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-400">{l.category}</p>
                {analytics[l.id] && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400">👁️ {analytics[l.id].views}</span>
                    <span className="text-xs text-gray-400">❤️ {analytics[l.id].saves}</span>
                    {analytics[l.id].offers > 0 && <span className="text-xs text-orange-500">💰 {analytics[l.id].offers} offers</span>}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${l.status === "active" ? "bg-green-100 text-green-700" : l.status === "pending_purchase" ? "bg-amber-100 text-amber-700" : l.status === "paused" ? "bg-gray-200 text-gray-600" : l.status === "expired" ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}`}>
                  {l.status === "active" ? "Active" : l.status === "pending_purchase" ? "Being Bought" : l.status === "paused" ? "Paused" : l.status === "expired" ? "Expired" : "Sold"}
                </span>
                <div className="flex items-center gap-2">
                  {l.status === "active" && (
                    <>
                      <button onClick={() => navigate(`/sell?edit=${l.id}`)}
                        className="flex items-center gap-1 text-xs text-blue-500 font-medium hover:text-blue-700">
                        <Pencil size={12} /> Edit
                      </button>
                      <button onClick={() => bumpToTop(l.id)}
                        title="Move to top of listings"
                        className="flex items-center gap-1 text-xs text-purple-500 font-medium hover:text-purple-700">
                        <ArrowUp size={12} /> Bump
                      </button>
                      <button onClick={() => pauseListing(l.id)}
                        className="flex items-center gap-1 text-xs text-gray-500 font-medium hover:text-gray-700">
                        <PauseCircle size={13} /> Pause
                      </button>
                      <button onClick={() => markSold(l.id)}
                        className="flex items-center gap-1 text-xs text-green-600 font-medium hover:text-green-700">
                        <CheckCircle size={13} /> Sold
                      </button>
                    </>
                  )}
                  {l.status === "paused" && (
                    <button onClick={() => resumeListing(l.id)}
                      className="flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-800">
                      <PlayCircle size={13} /> Resume
                    </button>
                  )}
                  {l.status === "pending_purchase" && (
                    <button onClick={() => markSold(l.id)}
                      className="flex items-center gap-1 text-xs text-amber-600 font-medium hover:text-amber-700">
                      ✅ Confirm Sale
                    </button>
                  )}
                  {l.status === "sold" && (
                    <button onClick={async () => {
                      await update(ref(db, `listings/${l.id}`), { status: "active" });
                      setListings(prev => prev.map(x => x.id === l.id ? { ...x, status: "active" } : x));
                    }} className="flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-700">
                      Relist
                    </button>
                  )}
                  {l.status === "expired" && (
                    <button onClick={async () => {
                      await update(ref(db, `listings/${l.id}`), { status: "active", lastExpiryReminderAt: null });
                      setListings(prev => prev.map(x => x.id === l.id ? { ...x, status: "active" } : x));
                    }} className="flex items-center gap-1 text-xs text-blue-600 font-medium hover:text-blue-800">
                      ▶️ Relist
                    </button>
                  )}
                  <button onClick={() => deleteListing(l.id)}
                    className="flex items-center gap-1 text-xs text-red-400 font-medium hover:text-red-600">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FAB */}
      <Link to="/sell"
        className="fixed bottom-24 right-4 bg-blue-600 text-white rounded-full p-4 shadow-lg flex items-center justify-center hover:bg-blue-700 active:scale-95 transition z-40">
        <Plus size={24} />
      </Link>
    </div>
  );
}
